import { AppTrackingTransparency } from 'capacitor-plugin-app-tracking-transparency';
import { useEffect } from 'react';
import { SentryMinimal } from '@bridebook/toolbox/src/sentry';
import { Capacitor } from '@capacitor/core';
import { Appsflyer, AppsflyerOptions } from '@ionic-native/appsflyer';
import { parseUserAgent } from '@bridebook/web/lib/app/utils/parse-user-agent';
import { env } from '@bridebook/web/lib/env';
import { getIsIOS as getIsIos } from '@bridebook/web/lib/mobile-app/selectors';
import { DevicePlatform } from '@bridebook/web/lib/types';
import { useSelector } from '@bridebook/web/lib/utils';

// Timeout for waiting for ATT authorization on iOS (in seconds)
const ATT_REQUEST_TIMEOUT_SEC = 15;

const initAppsflyerSdk = async (isIOS: boolean) => {
  if (!Appsflyer || !AppTrackingTransparency) return;

  const options: AppsflyerOptions = {
    isDebug: !process.env.LIVE,
    onInstallConversionDataListener: true,
    devKey: env.APPSFLYER_DEV_KEY,
    appId: isIOS ? env.APPSTORE_ID : undefined,
    waitForATTUserAuthorization: isIOS ? ATT_REQUEST_TIMEOUT_SEC : undefined,
  };

  try {
    // We initialise the Appsflyer SDK immediately, but on iOS we still need to
    // request ATT permission from the user. We init SDK first to ensure that all events are captured.
    await Appsflyer.initSdk(options);

    // iOS: Show the App Tracking Transparency prompt
    if (isIOS) {
      const { status } = await AppTrackingTransparency.getStatus();
      if (status === 'notDetermined') {
        await AppTrackingTransparency.requestPermission();
      }
    }
  } catch (e) {
    SentryMinimal().captureException(e, {
      tags: {
        source: 'CordovaTracking',
      },
    });
    /* eslint-disable no-console */
    console.log(e);
  }
};

const CordovaTracking = () => {
  const { isIos: isIosUserAgent } = parseUserAgent(window.navigator.userAgent);
  const isIosPlatform = useSelector(getIsIos);
  const isIos = isIosUserAgent || isIosPlatform;

  useEffect(() => {
    /**
     * When accessing app.bridebook.com directly (via Apple Mail for instance), the `bb-is-app` header will be set to 1 by CloudFront.
     * This causes this gated code to run, which will initialise the appsflyer SDK.
     * If the user is on an iOS device, `isIos` will be true, but `appId` will not be set, causing the following error:
     * > 'appId' is missing or empty
     *
     * This condition attempts to fix that scenario.
     */
    const platform = Capacitor.getPlatform();

    if (platform === DevicePlatform.WEB) {
      return;
    }

    initAppsflyerSdk(isIos);
  }, [isIos]);
  return null;
};

export default CordovaTracking;
